import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import RegisterForm from "src/components/auth/RegisterForm";
import useScrollToTop from "src/hooks/useScrollToTop";
import { useAuth } from "src/context/AuthContext";
import { useRegisterStore } from "src/store/registerStore";
import { ROUTES } from "src/routes/RouteConfig";

const BecomeTutor = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const { setUserType } = useRegisterStore();
  const [isLoading, setIsLoading] = useState(false);

  // Set user type to tutor when component mounts
  useEffect(() => {
    setUserType("tutor");
  }, [setUserType]);

  const handleSubmit = async (email: string, password: string, userType: string) => {
    setIsLoading(true);
    try {
      const result = await signUp(email, password, {
        user_type: userType,
        onboarding_completed: false,
      });

      if (result.error) {
        console.error("Registration error:", result.error);
        // Handle error (you might want to show a toast or error message)
        return;
      }

      // Type guard to check if result has data property
      if ('data' in result && result.data?.user && !result.data.user.email_confirmed_at) {
        // Email confirmation required
        navigate(ROUTES.EMAIL_CONFIRMATION.path, {
          state: { 
            email, 
            message: "Please check your email and click the confirmation link to complete your registration.",
            source: "become-tutor"
          },
        });
      } else if ('data' in result && result.data?.user) {
        // User is confirmed, redirect to onboarding
        navigate(ROUTES.ONBOARDING.path);
      }
    } catch (error) {
      console.error("Unexpected error during registration:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Content */}
              <div className="text-center lg:text-left">
                <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl lg:text-6xl">
                  Be the change at{" "}
                  <span className="text-rfpurple-600">rfLearn</span>
                </h1>
                <p className="mt-6 text-xl text-gray-600 leading-relaxed">
                  Learning is evolving. Impact the lives and journey of students in this era of AI with rfLearn.
                </p>
                
                {/* Benefits Grid */}
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-rfpurple-500 text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Competitive Pay</h3>
                      <p className="text-gray-600">Set your own rates and earn what you deserve</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-rfpurple-500 text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Flexible Schedule</h3>
                      <p className="text-gray-600">Choose when and how much you want to teach</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-rfpurple-500 text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Make an Impact</h3>
                      <p className="text-gray-600">Help students achieve their academic goals</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-rfpurple-500 text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">AI-Enhanced Tools</h3>
                      <p className="text-gray-600">Use cutting-edge technology to enhance learning</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Registration Form */}
              <div className="flex justify-center lg:justify-end">
                <div className="w-full max-w-md">
                  <RegisterForm
                    onSubmit={handleSubmit}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Features Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">
                Why Choose rfLearn?
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Join a platform that's revolutionizing education with AI-powered tools
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-rfpurple-100 text-rfpurple-600 mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Instant Matching
                </h3>
                <p className="text-gray-600">
                  Our AI algorithm connects you with students who need your expertise
                </p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-rfpurple-100 text-rfpurple-600 mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Performance Analytics
                </h3>
                <p className="text-gray-600">
                  Track your teaching effectiveness and student progress with detailed insights
                </p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-rfpurple-100 text-rfpurple-600 mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Community Support
                </h3>
                <p className="text-gray-600">
                  Join a community of educators and access ongoing professional development
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="bg-rfpurple-600 py-16">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Education?
            </h2>
            <p className="text-xl text-rfpurple-100 mb-8">
              Join the bandwagon and make a difference with rfLearn's AI-powered platform.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BecomeTutor;